import { FormattedMessage } from "react-intl";

import { useGetAssignedTeams } from "api/queries/quotation/useGetAssignedTeams";
import Badge from "components/v2/Badge/Badge";
import testIds from "constants/testIds";

interface TopNavBadgesProps {
  isMultiStop: boolean;
  isMultiDrop: boolean;
  numberOfStops: number;
  shipmentReference: string;
  isCustomBroker: boolean;
  showBrokerBadge: boolean;
  userType: string;
  isOffPlatform: boolean;
}

export default function TopNavBadges({
  isMultiStop,
  isMultiDrop,
  numberOfStops,
  shipmentReference,
  isCustomBroker,
  showBrokerBadge,
  userType,
  isOffPlatform,
}: TopNavBadgesProps) {
  const { data: assignedTeams = [] } = useGetAssignedTeams({
    id: shipmentReference!,
    config: { params: { already_assigned: true } },
    options: {
      enabled: userType === "provider",
    },
  });
  const assignedTeam = assignedTeams?.[0];
  const showAssignedBadge = !!assignedTeam && userType === "provider";
  const showCustomBrokerBadge = isCustomBroker && showBrokerBadge;
  const showMultiStopBadge = (isMultiStop || isMultiDrop) && numberOfStops > 0;
  const showOffPlatformBadge = isOffPlatform;

  const hasAtLeastOneBadge =
    showAssignedBadge || showCustomBrokerBadge || showMultiStopBadge || showOffPlatformBadge;

  return (
    <>
      {hasAtLeastOneBadge && (
        <div className="d-flex flex-wrap gap-sm mt-xs" data-testid={testIds.TOPNAV_V2_BADGES_CONTAINER}>
          {showOffPlatformBadge && (
            <Badge
              color="secondary-orange"
              type="status-badge"
              size="sm"
              label={
                <span data-testid={testIds.TOPNAV_V2_BADGE_OFF_PLATFORM_VALUE}>
                  <FormattedMessage id="general.offPlatformShipmentBadge" defaultMessage="Off-platform shipment" />
                </span>
              }
            />
          )}

          {showAssignedBadge && (
            <Badge
              color="pink"
              type="status-badge"
              size="sm"
              label={
                <span data-testid={testIds.TOPNAV_V2_BADGE_ASSIGNED_TEAM_VALUE}>
                  <FormattedMessage id="general.team" defaultMessage="Team" />
                  {": "}
                  {assignedTeam.team_name}
                </span>
              }
            />
          )}

          {showCustomBrokerBadge && (
            <Badge
              color="secondary-orange"
              type="status-badge"
              size="sm"
              label={
                <span data-testid={testIds.TOPNAV_V2_BADGE_CUSTOMS_BROKER_VALUE}>
                  <FormattedMessage id="general.customsBrokerView" defaultMessage="Viewing as Customs Broker" />
                </span>
              }
            />
          )}

          {showMultiStopBadge && (
            <Badge
              color="indigo"
              type="status-badge"
              size="sm"
              label={
                <span data-testid={testIds.TOPNAV_V2_BADGE_MULTISTOP_VALUE}>
                  <FormattedMessage id="new-request.schedule.multistop" defaultMessage="Multi-stop" />
                  {": "}
                  {numberOfStops} <FormattedMessage id="general.stops" defaultMessage="stops" />
                </span>
              }
            />
          )}
        </div>
      )}
    </>
  );
}
